/*
 * Copyright (c) 2020, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */
import {
  arrayFromCollection,
  getOwnPropertyDescriptor,
  MapCreate,
  isHostElement,
  getShadowRootFromHostElement,
} from '../shared/utils';
import {
  parentNodeGetter as parentNodeOriginal,
  textContextGetter as textContentOriginal,
  parentElementGetter as parentElementOriginal,
  hasChildNodes as hasChildNodesOriginal,
  getRootNode as getRootNodeOriginal,
  childNodesGetter as childNodesOriginal,
  firstChildGetter as firstChildOriginal,
  lastChildGetter as lastChildOriginal,
  ELEMENT_NODE,
  COMMENT_NODE,
  DOCUMENT_FRAGMENT_NODE,
} from '../env/node';
import { createStaticNodeList } from '../shared/static-node-list';

const assignedSlotGetter: (this: /* Slotable |*/ Node) => HTMLSlotElement | null = getOwnPropertyDescriptor(
  Element.prototype,
  'assignedSlot'
)!.get!;

export function isSlotElement(node: Node): node is HTMLSlotElement {
  return node instanceof HTMLSlotElement;
}

export function getFilteredChildNodes(node: Node): Node[] {
  if (isSlotElement(node)) {
    return node.assignedNodes({ flatten: true });
  } else if (isHostElement(node)) {
    return arrayFromCollection(childNodesOriginal.call(getShadowRootFromHostElement(node as HTMLElement)));
  }
  return arrayFromCollection(childNodesOriginal.call(node));
}

const firstChildDistortion = function firstChild(this: Node): ChildNode | null {
  const nodes = getFilteredChildNodes(this);
  return nodes.length > 0 ? (nodes[0] as ChildNode) : null;
};

const lastChildDistortion = function lastChild(this: Node): ChildNode | null {
  const nodes = getFilteredChildNodes(this);
  return nodes.length > 0 ? (nodes[nodes.length - 1] as ChildNode) : null;
};

const textContentDistortion = function textContent(this: Node): string {
  return getTextContent(this);
};

const parentNodeDistortion = function parentNode(this: Node): (Node & ParentNode) | null {
  const assignedSlot = assignedSlotGetter.call(this);
  // if node is slotted, jump into the slot node instead
  if (assignedSlot) {
    return assignedSlot;
  }
  const parentNode = parentNodeOriginal.call(this);
  // if walking up we encounter a shadowRoot, we skip it
  if (parentNode && parentNode instanceof ShadowRoot) {
    return parentNode.host;
  }
  return parentNode;
};

const parentElementDistortion = function parentElement(this: Node): Element | null {
  const assignedSlot = assignedSlotGetter.call(this);
  // if node is slotted, jump into the slot node instead
  if (assignedSlot) {
    return assignedSlot;
  }
  const parentNode = parentNodeOriginal.call(this);
  // if walking up we encounter a shadowRoot, we skip it
  if (parentNode && parentNode instanceof ShadowRoot) {
    return parentNode.host;
  }
  return parentElementOriginal.call(this);
};

const childNodesDistortion = function childNodes(this: Node): NodeListOf<Node> {
  return createStaticNodeList(getFilteredChildNodes(this)); // cast to NodeListOf<Node>
};

const hasChildNodesDistortion = function hasChildNodes(this: Node): boolean {
  return getFilteredChildNodes(this).length > 0;
};

const getRootNodeDistortion = function getRootNode(this: Node): Node | null {
  if (this.isConnected) {
    return this.ownerDocument; // Is this correct?
  }
  return getRootNodeOriginal.call(this);
};

function getTextContent(node: Node): string {
  switch (node.nodeType) {
    case ELEMENT_NODE:
    case DOCUMENT_FRAGMENT_NODE: {
      const childNodes = getFilteredChildNodes(node);
      let content = '';
      for (let i = 0, len = childNodes.length; i < len; i += 1) {
        const currentNode = childNodes[i];

        if (currentNode.nodeType !== COMMENT_NODE) {
          content += getTextContent(currentNode);
        }
      }
      return content;
    }
    default:
      return node.nodeValue as string;
  }
}

export default MapCreate([
  [firstChildOriginal, firstChildDistortion],
  [lastChildOriginal, lastChildDistortion],
  [textContentOriginal, textContentDistortion],
  [parentNodeOriginal, parentNodeDistortion],
  [parentElementOriginal, parentElementDistortion],
  [childNodesOriginal, childNodesDistortion],
  [hasChildNodesOriginal, hasChildNodesDistortion],
  [getRootNodeOriginal, getRootNodeDistortion],
]);
